import React, { useState, useEffect } from "react";
import {
  Card,
  CardHeader,
  CardTitle,
  CardContent,
  Button,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  Badge,
} from "@/components/ui";
import { Download, RefreshCw, ChevronDown, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import reportsService from "@/services/reportsService";
import { SubsidiaryFilter, DateRangeFilter, BudgetFilter } from "../filters";

/**
 * Budget Report Component
 *
 * Displays budget summary with utilization indicators and expandable transaction details.
 *
 * Requirements: 4.1-4.7
 */
const BudgetReport = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [budgets, setBudgets] = useState([]);
  const [expandedBudgets, setExpandedBudgets] = useState(new Set());

  // Set default date range to 3 months
  const getDefaultDateRange = () => {
    const today = new Date();
    const threeMonthsAgo = new Date();
    threeMonthsAgo.setMonth(threeMonthsAgo.getMonth() - 3);

    return {
      date_from: threeMonthsAgo.toISOString().split("T")[0],
      date_to: today.toISOString().split("T")[0],
    };
  };

  const [filters, setFilters] = useState({
    subsidiary_id: "",
    budget_ids: [],
    ...getDefaultDateRange(),
  });

  useEffect(() => {
    loadBudgetReport();
  }, []);

  const loadBudgetReport = async () => {
    try {
      setLoading(true);
      // The backend expects a comma-separated string for budget_ids, not a
      // repeated array query param, and empty subsidiary_id/date filters
      // must be omitted rather than sent as "".
      const params = {
        ...filters,
        subsidiary_id: filters.subsidiary_id || undefined,
        date_from: filters.date_from || undefined,
        date_to: filters.date_to || undefined,
        budget_ids: filters.budget_ids?.length
          ? filters.budget_ids.join(",")
          : undefined,
      };
      const response = await reportsService.getBudgetReport(params);

      if (response?.data?.success) {
        setBudgets(response.data.data.budgets);
      }
    } catch (error) {
      console.error("Failed to load budget report:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleApplyFilters = () => {
    loadBudgetReport();
  };

  const handleClearFilters = () => {
    setFilters({
      subsidiary_id: "",
      budget_ids: [],
      date_from: "",
      date_to: "",
    });
  };

  const toggleBudgetExpansion = (budgetId) => {
    const newExpanded = new Set(expandedBudgets);
    if (newExpanded.has(budgetId)) {
      newExpanded.delete(budgetId);
    } else {
      newExpanded.add(budgetId);
    }
    setExpandedBudgets(newExpanded);
  };

  const handleExport = async (format) => {
    try {
      setExporting(true);
      const exportData = {
        reportData: { budgets },
        reportType: "budget_report",
        reportTitle: `Budget Report (${filters.date_from} to ${filters.date_to})`,
        format,
        filters,
        auditTrail: "summary",
      };

      const response =
        format === "pdf"
          ? await reportsService.exportToPDF(exportData)
          : await reportsService.exportToExcel(exportData);

      if (response?.data?.success) {
        const downloadResponse = await reportsService.downloadExport(
          response.data.data.history_id,
        );
        const url = window.URL.createObjectURL(
          new Blob([downloadResponse.data]),
        );
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", response.data.data.filename);
        document.body.appendChild(link);
        link.click();
        link.remove();
      }
    } catch (error) {
      console.error("Export failed:", error);
    } finally {
      setExporting(false);
    }
  };

  const formatCurrency = (amount) => {
    return `₦${parseFloat(amount || 0).toLocaleString("en-NG", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`;
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-NG");
  };

  const getStatusColor = (utilization) => {
    if (utilization >= 90) return "text-red-600 bg-red-50";
    if (utilization >= 75) return "text-yellow-600 bg-yellow-50";
    return "text-green-600 bg-green-50";
  };

  const getStatusIndicator = (utilization) => {
    if (utilization >= 90) return "🔴";
    if (utilization >= 75) return "🟡";
    return "🟢";
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Budget Report</h1>
          <p className="text-gray-600 mt-1">
            Monitor budget utilization and spending
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => handleExport("pdf")}
            disabled={exporting || budgets.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            PDF
          </Button>
          <Button
            variant="outline"
            onClick={() => handleExport("excel")}
            disabled={exporting || budgets.length === 0}
          >
            <Download className="h-4 w-4 mr-2" />
            Excel
          </Button>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <SubsidiaryFilter
              value={filters.subsidiary_id}
              onChange={(value) =>
                setFilters({ ...filters, subsidiary_id: value })
              }
            />
            <DateRangeFilter
              dateFrom={filters.date_from}
              dateTo={filters.date_to}
              onDateFromChange={(value) =>
                setFilters({ ...filters, date_from: value })
              }
              onDateToChange={(value) =>
                setFilters({ ...filters, date_to: value })
              }
            />
            <BudgetFilter
              selectedBudgets={filters.budget_ids}
              onChange={(value) =>
                setFilters({ ...filters, budget_ids: value })
              }
              subsidiaryId={filters.subsidiary_id}
            />
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={handleApplyFilters} disabled={loading}>
              {loading ? (
                <>
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                  Loading...
                </>
              ) : (
                "Apply Filters"
              )}
            </Button>
            <Button variant="outline" onClick={handleClearFilters}>
              Clear Filters
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Budget Summary Table */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Summary</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="p-8 text-center">
              <RefreshCw className="h-8 w-8 animate-spin text-gray-400 mx-auto mb-4" />
              <p className="text-gray-600">Loading budget report...</p>
            </div>
          ) : budgets.length === 0 ? (
            <div className="p-8 text-center text-gray-500">
              No budgets found. Try adjusting your filters.
            </div>
          ) : (
            <div className="space-y-4">
              {budgets.map((budget) => (
                <div
                  key={budget.id}
                  className="border rounded-lg overflow-hidden"
                >
                  {/* Budget Summary Row */}
                  <div
                    className="p-4 bg-gray-50 cursor-pointer hover:bg-gray-100"
                    onClick={() => toggleBudgetExpansion(budget.id)}
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {expandedBudgets.has(budget.id) ? (
                          <ChevronDown className="h-5 w-5 text-gray-400" />
                        ) : (
                          <ChevronRight className="h-5 w-5 text-gray-400" />
                        )}
                        <div>
                          <h3 className="font-semibold text-lg">
                            {budget.name}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {budget.subsidiary_name || "All Subsidiaries"}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-6">
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Allocated</p>
                          <p className="font-semibold">
                            {formatCurrency(budget.allocated_amount)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Spent</p>
                          <p className="font-semibold text-red-600">
                            {formatCurrency(budget.spent_amount)}
                          </p>
                        </div>
                        <div className="text-right">
                          <p className="text-sm text-gray-600">Available</p>
                          <p className="font-semibold text-green-600">
                            {formatCurrency(budget.available_amount)}
                          </p>
                        </div>
                        <div className="text-right min-w-[100px]">
                          <p className="text-sm text-gray-600">Utilization</p>
                          <div className="flex items-center gap-2">
                            <span className="text-2xl">
                              {getStatusIndicator(
                                budget.utilization_percentage,
                              )}
                            </span>
                            <span
                              className={`font-semibold px-2 py-1 rounded ${getStatusColor(
                                budget.utilization_percentage,
                              )}`}
                            >
                              {budget.utilization_percentage.toFixed(1)}%
                            </span>
                          </div>
                        </div>
                      </div>
                    </div>

                    {/* Progress Bar */}
                    <div className="mt-3">
                      <div className="w-full bg-gray-200 rounded-full h-2">
                        <div
                          className={`h-2 rounded-full ${
                            budget.utilization_percentage >= 90
                              ? "bg-red-600"
                              : budget.utilization_percentage >= 75
                                ? "bg-yellow-500"
                                : "bg-green-600"
                          }`}
                          style={{
                            width: `${Math.min(budget.utilization_percentage, 100)}%`,
                          }}
                        />
                      </div>
                    </div>
                  </div>

                  {/* Expanded Transaction Details */}
                  {expandedBudgets.has(budget.id) && (
                    <div className="p-4 border-t">
                      <div className="mb-4">
                        <h4 className="font-semibold mb-2">Budget Breakdown</h4>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          <div>
                            <p className="text-gray-600">Committed</p>
                            <p className="font-medium">
                              {formatCurrency(budget.committed_amount)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Spent</p>
                            <p className="font-medium">
                              {formatCurrency(budget.spent_amount)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Variance</p>
                            <p
                              className={`font-medium ${
                                budget.variance >= 0
                                  ? "text-green-600"
                                  : "text-red-600"
                              }`}
                            >
                              {formatCurrency(budget.variance)}
                            </p>
                          </div>
                          <div>
                            <p className="text-gray-600">Status</p>
                            <Badge
                              variant={
                                budget.status === "active"
                                  ? "success"
                                  : "secondary"
                              }
                            >
                              {budget.status}
                            </Badge>
                          </div>
                        </div>
                      </div>

                      {/* Transactions */}
                      {budget.transactions &&
                        budget.transactions.length > 0 && (
                          <div>
                            <h4 className="font-semibold mb-2">
                              Recent Transactions ({budget.transactions.length})
                            </h4>
                            <div className="overflow-x-auto">
                              <Table>
                                <TableHeader>
                                  <TableRow>
                                    <TableHead>Date</TableHead>
                                    <TableHead>Description</TableHead>
                                    <TableHead>Type</TableHead>
                                    <TableHead className="text-right">
                                      Amount
                                    </TableHead>
                                    <TableHead>Status</TableHead>
                                  </TableRow>
                                </TableHeader>
                                <TableBody>
                                  {budget.transactions
                                    .slice(0, 10)
                                    .map((txn, idx) => (
                                      <TableRow key={idx}>
                                        <TableCell>
                                          {formatDate(txn.date)}
                                        </TableCell>
                                        <TableCell>{txn.description}</TableCell>
                                        <TableCell>
                                          <Badge variant="outline">
                                            {txn.type}
                                          </Badge>
                                        </TableCell>
                                        <TableCell className="text-right">
                                          {formatCurrency(txn.amount)}
                                        </TableCell>
                                        <TableCell>
                                          <Badge
                                            variant={
                                              txn.status === "posted"
                                                ? "success"
                                                : "secondary"
                                            }
                                          >
                                            {txn.status}
                                          </Badge>
                                        </TableCell>
                                      </TableRow>
                                    ))}
                                </TableBody>
                              </Table>
                            </div>
                          </div>
                        )}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default BudgetReport;
